import { ReactNode } from "react";

interface EntitySelectorTriggerContentProps {
  /** Display name of the selected entity, or undefined when nothing is selected. */
  displayName?: string;
  /** Text shown when nothing is selected. */
  placeholder: string;
  /** Extra content stacked under the name in the default two-line layout. */
  extraInfo?: ReactNode;
  /** Trailing value shown to the right of the name in the single-line layout. */
  value?: ReactNode;
  /** Renders name and value on one line instead of stacking name over extra info. */
  singleLine: boolean;
}

/**
 * Renders the closed trigger content of a single entity selector.
 * @param props - The selected entity's display values and the layout to use
 * @returns The trigger content, or the placeholder when nothing is selected
 */
export function EntitySelectorTriggerContent({
  displayName,
  placeholder,
  extraInfo,
  value,
  singleLine,
}: EntitySelectorTriggerContentProps) {
  if (displayName === undefined) {
    return <span>{placeholder}</span>;
  }

  if (singleLine) {
    return (
      <div className="flex w-full min-w-0 items-center gap-2">
        <span className="min-w-0 grow shrink truncate">{displayName}</span>
        {value && (
          <span className="min-w-0 shrink truncate tabular-nums">{value}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start min-w-0 overflow-hidden">
      <span className="truncate w-full">{displayName}</span>
      {extraInfo && (
        <div className="truncate w-full [&>*]:truncate [&>*]:overflow-hidden">
          {extraInfo}
        </div>
      )}
    </div>
  );
}
